import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { FiChevronDown, FiCode } from "react-icons/fi";
import { HiOutlineBeaker } from "react-icons/hi";
import { LuUtensilsCrossed } from "react-icons/lu";
import Navigation from "../components/Navigation";
import FooterSection from "../components/LandingPageComponents/FooterSection";
import ganeshImg from "../assets/Ganesh_bagler.jpg";
import jayantImg from "../assets/jkk.png";
import heroBg from "../assets/previous_bg_image.jpg";

const heroTags = [
  "DATA",
  "SCIENCE",
  "TECHNOLOGY",
  "COMPUTATIONAL GASTRONOMY",
];

const milestones = [
  {
    year: "2018",
    title: "CoSyLab Research",
    description:
      "Foundational studies in culinary informatics and flavor network theory.",
  },
  {
    year: "2020",
    title: "FlavorDB & RecipeDB Launch",
    description:
      "Release of the first large-scale flavor molecule database to the scientific community.",
  },
  {
    year: "2021",
    title: "Ingredient Network Analysis",
    description:
      "Over 1,000 ingredients mapped with chemical and culinary context.",
  },
  {
    year: "2023",
    title: "Network Effect",
    description:
      "The dataset grows to encompass global cuisines - a continuously evolving knowledge layer shaped by research and real-world use.",
  },
  {
    year: "2025",
    title: "Public Launch",
    description:
      "Accessible ecosystem - tools for chefs, developers, researchers, and food lovers to explore food through data",
    featured: true,
  },
  // {
  //   year: "2023",
  //   title: "Network Effect",
  //   description:
  //     "The dataset grows to encompass global cuisines - a continuously evolving knowledge layer shaped by research and real-world use.",
  // },
];

const statCards = [
  { value: "118K+", label: "VALIDATED RECIPES" },
  { value: "1,000+", label: "UNIQUE INGREDIENTS" },
  { value: "2,400+", label: "FLAVOR MOLECULES" },
  { value: "∞", label: "CONNECTED DATASETS", highlighted: true },
];

const partnerCards = [
  {
    icon: <HiOutlineBeaker className="h-8 w-8 text-cyan-300" />,
    title: "Researchers",
    description:
      "Access extensive molecular libraries and citation data for scientific validation of food pairing theories.",
    points: ["Molecular Fingerprinting", "Chemical Similarity Maps"],
  },
  {
    icon: <FiCode className="h-8 w-8 text-cyan-300" />,
    title: "Developers",
    description:
      "Integrate powerful gastronomy engines into your applications via our enterprise-grade REST APIs.",
    points: ["RESTful Endpoints", "SDK Support"],
  },
  {
    icon: <LuUtensilsCrossed className="h-8 w-8 text-cyan-300" />,
    title: "Chefs & Creators",
    description:
      "Discover unexpected ingredient combinations and push the boundaries of culinary creative expression.",
    points: ["Flavor Pairing Engine", "Recipe Transformation"],
  },
];

const researchTopics = [
  {
    title: "Ingredient Networks",
    description: "Mapping the global distribution of ingredient usage.",
  },
  {
    title: "Flavor Pairing",
    description: "Developing algorithms to predict molecular affinity.",
  },
  {
    title: "Health Informatics",
    description: "Connecting flavor profiles with nutritional biomarkers.",
  },
];

const publications = [
  {
    title: "Computational gastronomy: capturing culinary creativity by making food computable",
    link: "https://www.nature.com/articles/s41540-024-00399-5",
  },
  {
    title: "Computational gastronomy: A data science approach to food",
    link: "https://link.springer.com/article/10.1007/s12038-021-00248-1",
  },
  {
    title: "RecipeDB: a resource for exploring recipes",
    link: "https://academic.oup.com/database/article-abstract/doi/10.1093/database/baaa077/6006228",
  },
  {
    title: "FlavorDB: a database of flavor molecules",
    link: "https://academic.oup.com/nar/article/46/D1/D1210/4559748?guestAccessKey=",
  },
  {
    title: "FlavorDB2: an updated database of flavor molecules",
    link: "https://ift.onlinelibrary.wiley.com/doi/abs/10.1111/1750-3841.17298",
  },
  {
    title: "Dish Detection in Indian Food Platters: A Computational Framework for Diet Management",
    link: "https://link.springer.com/chapter/10.1007/978-3-031-58181-6_20",
  },
  {
    title: "Object detection in indian food platters using transfer learning with yolov4",
    link: "https://ieeexplore.ieee.org/abstract/document/9814702/",
  },
  {
    title: "Analysis of food pairing in the regional cuisines of India",
    link: "https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0139539",
  },
  {
    title: "Spices form the basis of food pairing in Indian cuisine",
    link: "https://arxiv.org/abs/1502.03815",
  },
  {
    title: "Data-driven analysis of biomedical literature suggests broad-spectrum benevolence of culinary herbs and spices",
    link: "https://www.biorxiv.org/content/10.1101/276105.abstract",
  },
  {
    title: "SpiceRx: an integrated resource for the health impacts of culinary spices and herbs",
    link: "https://www.biorxiv.org/content/10.1101/273599.abstract",
  },
  {
    title: "ToxinPredictor: Computational models to predict the toxicity of molecules",
    link: "https://www.bing.com/ck/a?!&&p=54516167347084c6abf3ef604c411a3b4c7000106ecab036d9f2cba36fc5a016JmltdHM9MTc3NDIyNDAwMA&ptn=3&ver=2&hsh=4&fclid=01e24052-a1ce-652e-01ec-5576a0b16411&psq=ToxinPredictor%3a+Computational+models+to+predict+the+toxicity+of+molecules&u=a1aHR0cHM6Ly93d3cuc2NpZW5jZWRpcmVjdC5jb20vc2NpZW5jZS9hcnRpY2xlL3BpaS9TMDA0NTY1MzUyNDAyODA3OA",
  },
  {
    title: "Machine learning and natural language processing models to predict the extent of food processing",
    link: "https://www.bing.com/ck/a?!&&p=993d37cf994d87b7f2ad09c12745c21a9839eb84f35efe4d3622cb6a14d77a86JmltdHM9MTc3NDIyNDAwMA&ptn=3&ver=2&hsh=4&fclid=01e24052-a1ce-652e-01ec-5576a0b16411&psq=Machine+learning+and+natural+language+processing+models+to+predict+the+extent+of+food+processing&u=a1aHR0cHM6Ly93d3cuc2NpZW5jZWRpcmVjdC5jb20vc2NpZW5jZS9hcnRpY2xlL2Ficy9waWkvUzA4ODkxNTc1MjUwMDc1Mzc",
  },
  {
    title: "The Digital Sous Chef -- A Comparative Study on Fine-Tuning Language Models for Recipe Generation",
    link: "https://www.catalyzex.com/paper/the-digital-sous-chef-a-comparative-study-on",
  },
  {
    title: "Application of machine learning to predict food processing level using Open Food Facts",
    link: "https://arxiv.org/abs/2512.17169",
  },
  {
    title: "Computational Gastronomy: The Emerging Science of Food, Flavors, Nutrition, and Personalized Health",
    link: "https://dl.acm.org/doi/abs/10.1145/3746264.3760484",
  },
  {
    title: "UmamiPredict: a machine learning model to predict the umami taste of molecules and peptides",
    link: "https://link.springer.com/article/10.1007/s11030-025-11371-8",
  },
  {
    title: "MultiTox: A sequence-based stacked ensemble model for multiclass protein toxin classification",
    link: "https://www.sciencedirect.com/science/article/pii/S0141813025079565",
  },
  {
    title: "Computational gastronomy: an artificial intelligence-driven approach to recipes, flavors, nutrition, health, and sustainability",
    link: "https://repository.iiitd.edu.in/xmlui/handle/123456789/1766",
  },
  {
    title: "AllerStack: Predicting Allergenic Proteins with a Stacked Ensemble Approach",
    link: "https://www.biorxiv.org/content/10.1101/2025.06.22.660976.abstract",
  },
  {
    title: "Deep learning based named entity recognition models for recipes",
    link: "https://aclanthology.org/2024.lrec-main.406/",
  },
  {
    title: "Cultural context shapes the carbon footprints of recipes",
    link: "https://www.sciencedirect.com/science/article/pii/S1878450X24001501",
  },
  {
    title: "Machine learning models to predict the sweetness of molecules",
    link: "https://www.sciencedirect.com/science/article/pii/S0010482522011490",
  },
  {
    title: "Computational creativity: artificial intelligence models for the generation and classification of affective artwork and their human evaluation",
    link: "https://repository.iiitd.edu.in/jspui/handle/123456789/1356",
  },
  {
    title: "Blockchain for food: farm to fork",
    link: "https://repository.iiitd.edu.in/xmlui/handle/123456789/1455",
  },
  {
    title: "Cuisine fusion",
    link: "https://repository.iiitd.edu.in/jspui/handle/123456789/1614",
  },
  {
    title: "Novel recipe generation: cuisine constraint",
    link: "https://repository.iiitd.edu.in/xmlui/handle/123456789/1419",
  },
  {
    title: "Computational gastronomy: Turing test for chef framework",
    link: "https://repository.iiitd.edu.in/xmlui/handle/123456789/1595",
  },
  {
    title: "LLMs and their applications for novel recipe generation",
    link: "https://repository.iiitd.edu.in/jspui/handle/123456789/1611",
  },
  {
    title: "Computational gastronomy: novel recipe generation with constraint optimization",
    link: "https://repository.iiitd.edu.in/jspui/handle/123456789/1576",
  },
  {
    title: "A mobile application for dish detection in food platters by deploying an object detection computational protocol",
    link: "https://repository.iiitd.edu.in/jspui/handle/123456789/1339",
  },
  {
    title: "Ratatouille: A tool for novel recipe generation",
    link: "https://ieeexplore.ieee.org/abstract/document/9814641/",
  },
  {
    title: "A generative grammar of cooking",
    link: "https://arxiv.org/abs/2211.09059",
  },
  {
    title: "Network biology of inflammatory bowel diseases",
    link: "https://repository.iiitd.edu.in/jspui/handle/123456789/1282",
  },
  {
    title: "A named entity-based approach to model recipes",
    link: "https://ieeexplore.ieee.org/abstract/document/9094103/",
  },
  {
    title: "Nutritional profile estimation in cooking recipes",
    link: "https://ieeexplore.ieee.org/abstract/document/9094134/",
  },
  {
    title: "Building hierarchically disentangled language models for text generation with named entities",
    link: "https://aclanthology.org/2020.coling-main.3/",
  },
  {
    title: "Classification of cuisines from sequentially structured recipes",
    link: "https://ieeexplore.ieee.org/abstract/document/9094114/",
  },
  {
    title: "Hierarchical clustering of world cuisines",
    link: "https://ieeexplore.ieee.org/abstract/document/9094121/",
  },
  {
    title: "BitterSweet: Building machine learning models for predicting the bitter and sweet taste of small molecules",
    link: "https://www.nature.com/articles/s41598-019-43664-y",
  },
  {
    title: "Computational models for the evolution of world cuisines",
    link: "https://ieeexplore.ieee.org/abstract/document/8750964/",
  },
  {
    title: "Data-driven analysis of biomedical literature suggests broad-spectrum benefits of culinary herbs and spices",
    link: "https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0198030",
  },
  {
    title: "Culinary evolution models for Indian cuisines",
    link: "https://www.sciencedirect.com/science/article/pii/S0378437118302656",
  },
  {
    title: "Data-driven investigations of culinary patterns in traditional recipes across the world",
    link: "https://ieeexplore.ieee.org/abstract/document/8402036/",
  },
  {
    title: "A hierarchical anatomical classification schema for prediction of phenotypic side effects",
    link: "https://journals.plos.org/plosone/article?id=10.1371/journal.pone.0193959",
  },
  {
    title: "Application of pattern mining on data of flavor molecules, their percepts and molecular features",
    link: "https://www.academia.edu/download/95415972/MT16124_Biswadeep.pdf",
  },
];

const AboutPage = () => {
  const publicationsCarouselRef = useRef(null);
  const isDraggingRef = useRef(false);
  const hasDraggedRef = useRef(false);
  const dragStartXRef = useRef(0);
  const dragStartScrollLeftRef = useRef(0);

  const scrollPublications = (direction) => {
    if (!publicationsCarouselRef.current) return;

    publicationsCarouselRef.current.scrollBy({
      left: direction === "left" ? -360 : 360,
      behavior: "smooth",
    });
  };

  const handleCarouselWheel = (event) => {
    const container = publicationsCarouselRef.current;
    if (!container) return;

    if (Math.abs(event.deltaY) > Math.abs(event.deltaX)) {
      container.scrollBy({
        left: event.deltaY,
        behavior: "auto",
      });
    }
  };

  const handleCarouselPointerDown = (event) => {
    const container = publicationsCarouselRef.current;
    if (!container) return;

    isDraggingRef.current = true;
    hasDraggedRef.current = false;
    dragStartXRef.current = event.clientX;
    dragStartScrollLeftRef.current = container.scrollLeft;
  };

  const handleCarouselPointerMove = (event) => {
    const container = publicationsCarouselRef.current;
    if (!container || !isDraggingRef.current) return;

    const deltaX = event.clientX - dragStartXRef.current;
    if (Math.abs(deltaX) > 5) {
      hasDraggedRef.current = true;
    }
    container.scrollLeft = dragStartScrollLeftRef.current - deltaX;
  };

  const handleCarouselPointerUp = () => {
    isDraggingRef.current = false;
  };

  const handleLinkClick = (event) => {
    if (hasDraggedRef.current) {
      event.preventDefault(); // Prevent opening link if we were dragging
    }
  };

  return (
    <main className="min-h-screen bg-[#0b0b0b] text-white">
      <Navigation stay={true} />

      <section className="relative overflow-hidden pt-28">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-55"
          style={{ backgroundImage: `url(${heroBg})` }}
        />
        <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(11,11,11,0.76)_0%,rgba(11,11,11,0.3)_36%,rgba(11,11,11,0.95)_100%)]" />

        <div className="relative mx-auto flex min-h-[760px] max-w-7xl flex-col items-center px-6 pb-20 pt-14 text-center sm:px-8 lg:px-12">
          <div className="flex flex-wrap justify-center gap-3">
            {heroTags.map((tag) => (
              <span
                key={tag}
                className="rounded-full border border-[#bdfb5d]/20 bg-[#bdfb5d]/8 px-5 py-2 text-[11px] tracking-[0.28em] text-[#c7ff63]"
              >
                {tag}
              </span>
            ))}
          </div>

          <h1 className="mt-12 max-w-5xl text-5xl font-semibold leading-[0.95] tracking-[-0.05em] sm:text-6xl lg:text-[6.8rem]">
            Reimagining food
            <br />
            through lens of data
          </h1>

          <p className="mt-10 max-w-4xl text-lg leading-9 text-white/70 sm:text-2xl">
            Unlocking the molecular secrets of taste through the world&apos;s
            most advanced computational gastronomy platform.
          </p>

          <div className="mt-14 flex h-14 w-14 items-center justify-center rounded-full border border-[#c7ff63]/30 bg-black/20">
            <FiChevronDown className="h-8 w-8 text-[#c7ff63]" />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-12 sm:px-8 lg:px-12">
        <div className="grid gap-14 lg:grid-cols-[0.95fr_1.05fr] lg:items-start lg:gap-10">
          <div>
            <p className="text-sm tracking-[0.28em] text-white/85">OUR STORY</p>
            <h2 className="mt-10 max-w-[5ch] text-5xl font-semibold leading-[0.92] tracking-[-0.06em] sm:text-6xl lg:text-[5.8rem]">
              Born from research,
              <br />
              Built for everyone
            </h2>

            <div className="mt-24 flex flex-wrap gap-12 sm:gap-16">
              <div>
                <p className="text-4xl font-semibold leading-none text-[#c7ff63] sm:text-5xl lg:text-[4.25rem]">
                  11+ Years
                </p>
                <p className="mt-4 text-xs tracking-[0.24em] text-white/55">
                  ACTIVE RESEARCH
                </p>
              </div>
              <div>
                <p className="text-4xl font-semibold leading-none text-[#c7ff63] sm:text-5xl lg:text-[4.25rem]">
                  46+ Papers
                </p>
                <p className="mt-4 text-xs tracking-[0.24em] text-white/55">
                  PEER REVIEWED
                </p>
              </div>
            </div>
          </div>

          <div className="lg:pt-2">
            <p className="max-w-4xl text-2xl font-light italic leading-[1.8] text-white/92 sm:text-[2.1rem]">
              Food data shouldn&apos;t just exist - it should be explored,
              understood, and experienced.
            </p>

            <div className="mt-12 grid gap-6 sm:grid-cols-2 sm:items-start">
              <div>
                <div className="rounded-t-[28px] bg-[#090909] px-8 py-6">
                  <p className="text-2xl font-semibold sm:text-[2.1rem]">
                    Dr. Ganesh Bagler
                  </p>
                  <p className="mt-2 text-sm tracking-[0.22em] text-[#c7ff63]">
                    FOUNDER
                  </p>
                </div>
                <div className="overflow-hidden rounded-b-[28px] bg-[#111]">
                  <img
                    src={ganeshImg}
                    alt="Dr. Ganesh Bagler"
                    className="h-[360px] w-full object-cover object-top lg:h-[430px]"
                  />
                </div>
              </div>

              <div>
                <div className="rounded-t-[28px] bg-[#090909] px-8 py-6">
                  <p className="text-2xl font-semibold sm:text-[2.1rem]">
                    Jayant Khandare
                  </p>
                  <p className="mt-2 text-sm tracking-[0.22em] text-[#c7ff63]">
                    CO-FOUNDER
                  </p>
                </div>
                <div className="overflow-hidden rounded-b-[28px] bg-[#dcdcf4]">
                  <img
                    src={jayantImg}
                    alt="Jayant Khandare"
                    className="h-[360px] w-full object-cover object-top lg:h-[430px]"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* <section className="mx-auto max-w-7xl px-6 py-20 sm:px-8 lg:px-12">
        <div className="text-center">
          <p className="text-sm tracking-[0.34em] text-[#c7ff63]">
            DEVELOPMENT ROADMAP
          </p>
        </div>

        <div className="mt-16 grid gap-x-10 gap-y-14 md:grid-cols-3">
          {milestones.slice(0, 3).map((item) => (
            <div key={item.year} className="text-center">
              <p className="text-5xl font-semibold tracking-[-0.04em] text-white/28">
                {item.year}
              </p>
              <div className="mx-auto mt-8 h-4 w-4 rounded-full bg-[#c7ff63] shadow-[0_0_20px_rgba(199,255,99,0.95)]" />
              <h3 className="mt-8 text-2xl font-semibold">{item.title}</h3>
              <p className="mx-auto mt-4 max-w-sm text-lg leading-8 text-white/55">
                {item.description}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-10 grid gap-x-10 gap-y-14 border-t border-white/12 pt-12 md:grid-cols-3">
          <div className="md:col-start-2 text-center">
            <p className="text-6xl font-semibold tracking-[-0.04em] text-[#c7ff63]">
              {milestones[3].year}
            </p>
            <div className="mx-auto mt-8 h-4 w-4 rounded-full bg-[#c7ff63] shadow-[0_0_20px_rgba(199,255,99,0.95)]" />
            <h3 className="mt-8 text-3xl font-semibold">{milestones[3].title}</h3>
            <p className="mx-auto mt-4 max-w-md text-lg leading-8 text-white/55">
              {milestones[3].description}
            </p>
          </div>

          <div className="text-center md:col-start-3">
            <p className="text-5xl font-semibold tracking-[-0.04em] text-white/28">
              {milestones[4].year}
            </p>
            <div className="mx-auto mt-8 h-4 w-4 rounded-full bg-[#c7ff63] shadow-[0_0_20px_rgba(199,255,99,0.95)]" />
            <h3 className="mt-8 text-2xl font-semibold">{milestones[4].title}</h3>
            <p className="mx-auto mt-4 max-w-sm text-lg leading-8 text-white/55">
              {milestones[4].description}
            </p>
          </div>
        </div>
      </section> */}


      <section className="mx-auto max-w-5xl px-6 py-20 sm:px-8 lg:px-12">
        <div className="text-center">
          <p className="text-sm tracking-[0.34em] text-[#c7ff63]">
            DEVELOPMENT ROADMAP
          </p>
        </div>

        <div className="relative mt-20">
          {/* Vertical Line */}
          <div className="absolute left-1/2 top-0 h-full w-[2px] -translate-x-1/2 bg-gradient-to-b from-transparent via-[#c7ff63]/40 to-transparent" />

          <div className="space-y-20">
            {milestones.map((item, index) => {
              const isLeft = index % 2 === 0;

              return (
                <div
                  key={item.year}
                  className={`relative flex items-center ${isLeft ? "justify-start" : "justify-end"
                    }`}
                >
                  {/* Content Card */}
                  <div
                    className={`w-full md:w-[45%] ${isLeft ? "text-right pr-10" : "text-left pl-10"
                      }`}
                  >
                    <p className={`text-4xl font-semibold ${item.featured ? "text-[#c7ff63]" : "text-white/30"
                      }`}>
                      {item.year}
                    </p>

                    <h3 className="mt-4 text-2xl font-semibold">
                      {item.title}
                    </h3>
                    <p className="mt-3 text-lg text-white/55">
                      {item.description}
                    </p>
                  </div>

                  {/* Dot */}
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                    <div className="h-5 w-5 rounded-full bg-[#c7ff63] shadow-[0_0_25px_rgba(199,255,99,0.95)]" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>


      <section className="mx-auto max-w-7xl px-6 py-16 sm:px-8 lg:px-12">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {statCards.map((item) => (
            <div
              key={item.label}
              className={`rounded-[24px] px-10 py-12 ${item.highlighted
                ? "bg-[#c7ff63] text-[#314400]"
                : "bg-[#202020] text-white"
                }`}
            >
              <p
                className={`text-5xl font-semibold tracking-[-0.04em] ${item.highlighted ? "text-[#314400]" : "text-[#c7ff63]"
                  }`}
              >
                {item.value}
              </p>
              <p
                className={`mt-6 text-sm tracking-[0.22em] ${item.highlighted ? "text-[#314400]/65" : "text-white/55"
                  }`}
              >
                {item.label}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12">
        <p className="text-sm tracking-[0.34em] text-[#c7ff63]">
          ECOSYSTEM PARTNERS
        </p>
        <h2 className="mt-8 text-4xl font-semibold tracking-[-0.04em] sm:text-5xl lg:text-[4rem]">
          Anyone with a hunger for curiosity
        </h2>

        <div className="mt-16 grid gap-8 lg:grid-cols-3">
          {partnerCards.map((item) => (
            <div
              key={item.title}
              className="rounded-[28px] bg-[#121212] p-10 shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]"
            >
              {item.icon}
              <h3 className="mt-10 text-3xl font-semibold">{item.title}</h3>
              <p className="mt-6 text-lg leading-9 text-white/58">
                {item.description}
              </p>
              <div className="mt-10 space-y-4">
                {item.points.map((point) => (
                  <div key={point} className="flex items-center gap-3 text-lg text-white/8">
                    <span className="text-[#c7ff63]">✓</span>
                    <span className="text-white/75">{point}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-24 sm:px-8 lg:px-12">
        <div className="grid gap-16 xl:grid-cols-[0.8fr_1.5fr]">
          <div>
            <p className="text-sm tracking-[0.34em] text-[#c7ff63]">
              RESEARCH FOCUS
            </p>
            <div className="mt-12 space-y-10">
              {researchTopics.map((topic, index) => (
                <div
                  key={topic.title}
                  className={index < researchTopics.length - 1 ? "border-b border-white/8 pb-8" : ""}
                >
                  <h3 className="text-2xl font-semibold">{topic.title}</h3>
                  <p className="mt-3 text-lg text-white/55">
                    {topic.description}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <div className="min-w-0">
            <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
              <h2 className="text-4xl font-semibold tracking-[-0.04em] sm:text-5xl">
                Research Papers
              </h2>
              <div className="flex items-center gap-3">
                <p className="text-sm tracking-[0.28em] text-[#c7ff63]">
                  VIEW ALL PUBLICATIONS
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => scrollPublications("left")}
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-white/12 bg-[#181818] text-white/80 transition-all duration-300 hover:bg-[#232323] hover:text-white"
                    aria-label="Scroll publications left"
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    onClick={() => scrollPublications("right")}
                    className="flex h-11 w-11 items-center justify-center rounded-full border border-white/12 bg-[#181818] text-white/80 transition-all duration-300 hover:bg-[#232323] hover:text-white"
                    aria-label="Scroll publications right"
                  >
                    →
                  </button>
                </div>
              </div>
            </div>

            <div className="relative mt-12">
              <div className="pointer-events-none absolute inset-y-0 left-0 z-10 hidden w-10 bg-gradient-to-r from-[#0b0b0b] to-transparent lg:block" />
              <div className="pointer-events-none absolute inset-y-0 right-0 z-10 hidden w-10 bg-gradient-to-l from-[#0b0b0b] to-transparent lg:block" />

              <div
                ref={publicationsCarouselRef}
                onWheel={handleCarouselWheel}
                onPointerDown={handleCarouselPointerDown}
                onPointerMove={handleCarouselPointerMove}
                onPointerUp={handleCarouselPointerUp}
                onPointerLeave={handleCarouselPointerUp}
                className="[&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] w-full cursor-grab overflow-x-auto overscroll-x-contain touch-pan-x active:cursor-grabbing"
              >
                <div className="flex w-max gap-6 pb-4 pr-6">
                  {publications.map((item) => (
                    <a
                      key={item.title}
                      href={item.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      draggable={false}
                      onClick={handleLinkClick}
                      className="group flex w-[300px] shrink-0 flex-col rounded-[28px] bg-[#232323] p-8 transition-all duration-300 hover:scale-[1.02] hover:bg-[#2a2a2a] sm:w-[320px] lg:w-[336px]"
                    >
                      <p className="text-sm tracking-[0.16em] text-[#c7ff63]">
                        PUBLICATION
                      </p>
                      <h3
                        className="mt-4 min-h-[5.8rem] overflow-hidden text-2xl font-semibold leading-tight"
                        style={{
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {item.title}
                      </h3>
                      <div className="mt-auto pt-12 text-4xl text-cyan-300 transition-transform duration-300 group-hover:translate-x-1">
                        →
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative overflow-hidden bg-[#c7ff63]">
        <div className="absolute inset-0 opacity-25 [background:radial-gradient(circle_at_center,rgba(255,255,255,0.45)_0%,transparent_58%),linear-gradient(180deg,rgba(255,255,255,0.16),rgba(255,255,255,0))]" />
        <div className="relative mx-auto flex min-h-[520px] max-w-7xl flex-col items-center justify-center px-6 py-20 text-center sm:px-8 lg:px-12">
          <p className="text-sm tracking-[0.42em] text-[#5f7a12]">OUR VISION</p>
          <h2 className="mt-8 max-w-5xl text-4xl font-semibold leading-[1.02] tracking-[-0.05em] text-[#324600] sm:text-6xl lg:text-[5.25rem]">
            &quot;A world where food
            <br />
            knowledge is connected.&quot;
          </h2>
          <Link
            to="/pricing"
            className="mt-12 rounded-2xl bg-[#3e5605] px-10 py-5 text-2xl font-medium text-[#d8ff7b] transition hover:bg-[#344804]"
          >
            Join the Revolution
          </Link>
        </div>
      </section>

      <FooterSection />
    </main>
  );
};

export default AboutPage;




// import React from 'react';
// import Navigation from '../components/Navigation';
// import FooterSection from '../components/LandingPageComponents/FooterSection';
// import ganeshImg from '../assets/Ganesh_bagler.jpg';
// import jayantImg from '../assets/jayant_khandare.png';
// import teamImg from '../assets/team_foodoscope.png';

// const AboutPage = () => {
//   return (
//     <main className="min-h-screen flex flex-col">
//       <Navigation stay={true} />

//       {/* Hero Section with Dark Background and Pattern */}
//       <div className="w-full pt-28 pb-16 relative overflow-hidden" style={{ backgroundColor: '#2d2d2d' }}>
//         {/* Background Pattern */}
//         <div
//           className="absolute inset-0 w-full h-full"
//           style={{
//             // backgroundImage: `url("data:image/svg+xml,%3Csvg width='68' height='68' viewBox='0 0 68 68' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='32' y='24' width='4' height='20' fill='%2384cc16' opacity='0.8'/%3E%3Crect x='24' y='32' width='20' height='4' fill='%2384cc16' opacity='0.8'/%3E%3C/svg%3E")`,
//             backgroundRepeat: 'repeat',
//             backgroundSize: '68px 68px'
//           }}
//         />
//         <div className="max-w-4xl mx-auto px-4 text-center relative z-10">
//           <h1 className="text-5xl md:text-6xl font-bold mb-6 text-[#BDE958] font-['Sometype_Mono']">
//             About
//           </h1>
//           <h1 className="text-5xl md:text-6xl font-bold mb-8 text-[#BDE958] font-['Sometype_Mono']">
//             Foodoscope
//           </h1>
//           <p className="text-lg text-white max-w-2xl mx-auto font-['Sometype_Mono']">
//             Pioneering the intersection of food,<br />
//             data and artificial intelligence
//           </p>
//         </div>
//       </div>

//       {/* Content Section */}
//       <div className="flex-grow px-4 py-12 bg-gray-100">
//         <div className="max-w-4xl mx-auto space-y-8">
//           {/* Our Mission Section */}
//           <div className="bg-white rounded-3xl shadow-lg p-8 border-4 border-gray-800">
//             <div className="text-center mb-6">
//               <span className="inline-block bg-[#BDE958] text-black px-6 py-2 rounded-lg font-bold text-lg">
//                 Our Mission
//               </span>
//             </div>
//             <div className="space-y-4">
//               <div className="bg-[#BDE958] p-4 rounded-lg">
//                 <p className="text-black font-medium leading-relaxed">
//                   Foodoscope Technologies Pvt. Ltd. is a pioneering deep-tech startup at the intersection of food, data, and AI.
//                   Founded by researchers and technologists, we are building the missing data infrastructure for the food-tech
//                   and health-tech ecosystem.
//                 </p>
//               </div>
//               <div className="bg-[#BDE958] p-4 rounded-lg">
//                 <p className="text-black font-medium leading-relaxed">
//                   Our platform offers structured food datasets and APIs that power personalization, research, and innovation
//                   in the domains of nutrition, sustainability, and culinary intelligence. Whether you're developing an app,
//                   conducting public health studies, or creating intelligent kitchen systems — Foodoscope delivers the tools
//                   to make food computable.
//                 </p>
//               </div>
//             </div>
//           </div>

//           {/* Company Information Section */}

//           <div className="bg-white rounded-3xl shadow-lg p-8 border-4 border-gray-800">
//             <div className="text-center mb-6">
//               <span className="inline-block bg-[#BDE958] text-black px-6 py-2 rounded-lg font-bold text-lg">
//                 Company Information
//               </span>
//             </div>
//             <div className="bg-[#BDE958] p-6 rounded-lg">
//               <div className="space-y-3 text-black">
//                 <div>
//                   <span className="font-bold">Company:</span> Foodoscope Technologies Private Limited
//                 </div>
//                 <div>
//                   <span className="font-bold">Registered Office:</span><br />
//                   Foodoscope Technologies Pvt. Ltd.,<br />
//                   1A,2,3,6,7A, 2nd Floor, Indure House,<br />
//                   Savitri Cinema Complex, Greater Kailash 2, New Delhi 110048
//                 </div>
//                 <div>
//                   <span className="font-bold">CIN:</span> U62099DL2025PTC448083
//                 </div>
//                 <div>
//                   <span className="font-bold">E-Mail:</span> contact@foodoscope.com
//                 </div>
//                 <div>
//                   <span className="font-bold">Phone:</span> +91-7793829447
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Our Founders Section */}
//           <div className="bg-white rounded-3xl shadow-lg p-8 border-4 border-gray-800">
//             <div className="text-center mb-6">
//               <span className="inline-block bg-[#BDE958] text-black px-6 py-2 rounded-lg font-bold text-lg">
//                 Our Founders
//               </span>
//             </div>
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//               {/* Ganesh Bagler */}
//               <div className="bg-[#BDE958] rounded-lg overflow-hidden">
//                 <div className="w-full aspect-[4/5] overflow-hidden flex items-center justify-center bg-black">
//                   <img
//                     src={ganeshImg}
//                     alt="Ganesh Bagler"
//                     onError={(e) => {
//                       e.currentTarget.onerror = null;
//                       e.currentTarget.src = `${process.env.PUBLIC_URL}/assets/Ganesh_bagler.jpg`;
//                     }}
//                     className="w-full h-full object-cover object-top"
//                   />
//                 </div>
//                 <div className="p-4">
//                   <h3 className="text-lg font-bold text-black mb-1">Ganesh Bagler</h3>
//                   <p className="text-sm font-semibold text-black/70 mb-3">Founder</p>
//                   <ul className="space-y-1.5 text-sm text-black list-disc list-inside">
//                     <li>Computational Gastronomy Pioneer</li>
//                     <li>10 years of experience in building culinary databases, algorithms and applications</li>
//                   </ul>
//                 </div>
//               </div>

//               {/* Jayant Khandare */}
//               <div className="bg-[#BDE958] rounded-lg overflow-hidden">
//                 <div className="w-full aspect-[4/5] overflow-hidden flex items-center justify-center bg-[#E8ECDA]">
//                   <img
//                     src={jayantImg}
//                     alt="Jayant Khandare"
//                     onError={(e) => {
//                       e.currentTarget.onerror = null;
//                       e.currentTarget.src = `${process.env.PUBLIC_URL}/assets/jayant_khandare.png`;
//                     }}
//                     className="w-full h-full object-cover object-top scale-[1.2] origin-top transition-all duration-500 cursor-pointer"
//                   />
//                 </div>
//                 <div className="p-4">
//                   <h3 className="text-lg font-bold text-black mb-1">Jayant Khandare</h3>
//                   <p className="text-sm font-semibold text-black/70 mb-3">Co-founder</p>
//                   <ul className="space-y-1.5 text-sm text-black list-disc list-inside">
//                     <li>An expert in liquid biopsy and precision oncology</li>
//                     <li>Co-Founder and CSO of Actorius and 1Cell.AI</li>
//                     <li>Raised USD 20 Million (Series A) for 1Cell.AI</li>
//                   </ul>
//                 </div>
//               </div>
//             </div>
//           </div>

//           {/* Team Foodoscope Section */}
//           <div className="bg-white rounded-3xl shadow-lg p-8 border-4 border-gray-800">
//             <div className="text-center mb-6">
//               <span className="inline-block bg-[#BDE958] text-black px-6 py-2 rounded-lg font-bold text-lg">
//                 Team Foodoscope
//               </span>
//             </div>
//             <div className="bg-[#BDE958] rounded-lg overflow-hidden">
//               <div className="w-full h-64 md:h-80 overflow-hidden">
//                 <img
//                   src={teamImg}
//                   alt="Team Foodoscope"
//                   onError={(e) => {
//                     e.currentTarget.onerror = null;
//                     e.currentTarget.src = `${process.env.PUBLIC_URL}/assets/team_foodoscope.png`;
//                   }}
//                   className="w-full h-full object-cover object-center"
//                 />
//               </div>
//               <div className="p-4">
//                 <ul className="space-y-1.5 text-sm text-black list-disc list-inside">
//                   <li>As a lab spin off from IIIT-Delhi, we thrive on the talent of a large number of research interns.</li>
//                   <li>2025 Research Interns Team</li>
//                 </ul>
//               </div>
//             </div>
//           </div>
//         </div>
//       </div>

//       <FooterSection />
//     </main>
//   );
// };

// export default AboutPage;
